import React from 'react';
import { NotFoundError } from '../../../../errors/not-found-error';
import { ErrorBoundary } from '../../../error-boundary';
import { usePlay } from '../../hooks/use-play';
import { ClientPlayer } from './client-player';
import { PlayerErrorFallback } from '../player-error-fallback/player-error-fallback';
import { PlayerLoadingSpinner } from '../player-loading-spinner';
import { getAggregatedTitleImage } from '../../../../queries/ats/common';
import { VodPlayer } from '../vod-player/vod-player';
import { ProgramPlayer } from '../program-player/program-player';

interface ClientPlayerLoaderProps {
  platformTitleId: string;
  position: number | undefined;
  masterId?: string;
  providerIdentifier?: string;
}

export function ClientPlayerLoader({
  platformTitleId,
  providerIdentifier,
  position,
  masterId,
}: ClientPlayerLoaderProps) {
  const {
    data: { data, title, playbackOption, bookmark },
    isLoading,
    reportConsumption,
    stopConsumption,
  } = usePlay({ masterId, platformTitleId, providerIdentifier, isTrailer: false, startSession: true });

  if (isLoading) {
    return <PlayerLoadingSpinner />;
  }

  if (data === undefined) {
    throw new NotFoundError('No stream found');
  }

  if (title === undefined) {
    throw new NotFoundError('No title found');
  }

  if (playbackOption === undefined) {
    throw new NotFoundError('No playback option found');
  }

  switch (playbackOption.serviceType) {
    case 'vod':
    case 'svod':
      if (title.type === 'program') {
        throw new NotFoundError(`Unexpected title type, expected "episode" | "movie", but received "${title.type}"`);
      }

      return (
        <ErrorBoundary
          fallback={(props) => (
            <PlayerErrorFallback {...props} image={getAggregatedTitleImage(title.masterId, 'wide', 1400)} />
          )}
        >
          <VodPlayer
            data={data}
            title={title}
            playbackOption={playbackOption}
            position={position}
            reportConsumption={reportConsumption}
            stopConsumption={stopConsumption}
          />
        </ErrorBoundary>
      );
    case 'program':
      return (
        <ErrorBoundary
          fallback={(props) => (
            <PlayerErrorFallback {...props} image={getAggregatedTitleImage(title.masterId, 'wide', 1400)} />
          )}
        >
          <ProgramPlayer
            data={data}
            title={title}
            position={position ?? bookmark?.position}
            playbackOption={playbackOption}
            reportConsumption={reportConsumption}
            stopConsumption={stopConsumption}
          />
        </ErrorBoundary>
      );
    default:
      return (
        <ErrorBoundary
          fallback={(props) => (
            <PlayerErrorFallback {...props} image={getAggregatedTitleImage(title.masterId, 'wide', 1400)} />
          )}
        >
          <ClientPlayer
            data={data}
            title={title}
            position={position ?? bookmark?.position}
            playbackOption={playbackOption}
            reportConsumption={reportConsumption}
            stopConsumption={stopConsumption}
          />
        </ErrorBoundary>
      );
  }
}
